import {
  ALPHANUMERIC_MAX_CHARS,
  MAX_NUMERIC_STRING_LENGTH,
  SUPPORTED_DECIMAL_PLACES,
  TEXT_MAX_CHARS,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import yup from '@/libs/validation';

import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import Big from 'big.js';
import { useMemo } from 'react';
import { FieldValues, RegisterOptions } from 'react-hook-form';
import { ValidationError } from 'yup';

export type ValidationProperty = {
  maxChars?: number;
  minChars?: number;
  decimalPlaces?: number;
  minValue?: string;
  maxValue?: string;
  isRequired?: boolean;
  isVisible?: boolean;
  componentType: string;
  label?: string;
};

export type ValidationMessageKeys = {
  textMinLength?: string;
  textMaxLength?: string;
  alphanumericMinLength?: string;
  alphanumericMaxLength?: string;
  alphanumericPattern?: string;
  numericOnlyInteger?: string;
  numericIntegerPartMaxLengthExceeded?: string;
  numericIntegerPartMaxLengthExceededDecimal?: string;
  numericBetweenMinAndMax?: string;
  numericDecimalPartMaxLengthExceeded?: string;
  numericValueGreaterThanMax?: string;
  numericValueLessThanMin?: string;
};

const DEFAULT_MESSAGE_KEYS: Required<ValidationMessageKeys> = {
  textMinLength: 'general_inputs.validation_message.text.length',
  textMaxLength: 'general_inputs.validation_message.text.length',
  alphanumericMinLength:
    'general_inputs.validation_message.alphanumeric.length',
  alphanumericMaxLength:
    'general_inputs.validation_message.alphanumeric.length',
  alphanumericPattern: 'general_inputs.validation_message.alphanumeric.pattern',
  numericOnlyInteger: 'general_inputs.validation.message.numeric.only.integer',
  numericIntegerPartMaxLengthExceeded:
    'general_inputs.validation_message.numeric.integer_part.maxLength.exceeded.integer',
  numericIntegerPartMaxLengthExceededDecimal:
    'general_inputs.validation_message.numeric.integer_part.maxLength.exceeded.decimal',
  numericBetweenMinAndMax:
    'general_inputs.validation_message.numeric.between.minAndMax',
  numericDecimalPartMaxLengthExceeded:
    'general_inputs.validation_message.numeric.decimal_part.maxLength.exceeded',
  numericValueGreaterThanMax:
    'general_inputs.validation_message.numeric.value_greater_than_max',
  numericValueLessThanMin:
    'general_inputs.validation_message.numeric.value_less_than_min',
};

type SchemaCreator = (
  property: ValidationProperty,
  keys: Required<ValidationMessageKeys>,
  t: (key: string, params?: Record<string, unknown>) => string
) => yup.StringSchema<string | null | undefined>;

const createTextSchema: SchemaCreator = (property, keys, t) => {
  let schema = property.isRequired
    ? yup.string().required(t('required_input', { name: property.label }))
    : yup.string().optional();

  schema = schema.test('maxLength', function (value) {
    if (!value) return true;
    const max = property.maxChars ?? TEXT_MAX_CHARS;
    const actualLength = [...value].length;
    if (actualLength <= max) return true;
    return this.createError({
      message: t(keys.textMaxLength, {
        maxLength: property.maxChars ?? TEXT_MAX_CHARS,
        minLength: property.minChars ?? 0,
      }),
      type: 'error',
    });
  });

  schema = schema.test('minLength', function (value) {
    if (!value) return true;
    const actualLength = [...value].length;
    const min = property.minChars ?? 0;
    if (actualLength >= min) return true;
    return this.createError({
      message: t(keys.textMinLength, {
        maxLength: property.maxChars ?? TEXT_MAX_CHARS,
        minLength: property.minChars ?? 0,
      }),
      type: 'error',
    });
  });

  return schema;
};

const createAlphanumericSchema: SchemaCreator = (property, keys, t) => {
  let schema = property.isRequired
    ? yup.string().required(t('required_input', { name: property.label }))
    : yup.string().optional();

  schema = schema.test('pattern', function (value) {
    if (!value) return true;
    if (!value.match(/^[a-zA-Z0-9Ａ-Ｚａ-ｚ０-９]*$/)) {
      return this.createError({
        message: t(keys.alphanumericPattern),
        type: 'error',
      });
    }
    return true;
  });

  const max = property.maxChars ?? ALPHANUMERIC_MAX_CHARS;
  const min = property.minChars ?? 0;
  const maxLength = toCommaSeparated(max);
  const minLength = toCommaSeparated(min);

  schema = schema.test('maxLength', function (value) {
    if (!value) return true;
    if (value.length <= max) return true;
    return this.createError({
      message: t(keys.alphanumericMaxLength, {
        maxLength: maxLength,
        minLength: minLength,
      }),
      type: 'error',
    });
  });

  schema = schema.test('minLength', function (value) {
    if (!value) return true;
    if (value.length >= min) return true;
    return this.createError({
      message: t(keys.alphanumericMinLength, {
        maxLength: maxLength,
        minLength: minLength,
      }),
      type: 'error',
    });
  });

  return schema;
};

const createNumericSchema: SchemaCreator = (property, keys, t) => {
  let schema = property.isRequired
    ? yup.string().required(t('required_input', { name: property.label }))
    : yup.string().nullable();

  const isInteger = property.decimalPlaces === 0;
  const minValue = property.minValue;
  const maxValue = property.maxValue;

  const min = toCommaSeparated(minValue?.split('.')?.[0] ?? '');
  const max = toCommaSeparated(maxValue?.split('.')?.[0] ?? '');

  schema = schema.test(
    'numeric only integer',
    function method(value, { createError, path }) {
      if (!value) return true;
      const decimalPart = value.split('.')[1] ?? '';

      if (isInteger && decimalPart) {
        return createError({
          path,
          message: t(keys.numericOnlyInteger),
          type: 'error',
        });
      }

      return true;
    }
  );

  schema = schema.test(
    'decimal parts should be less than 3',
    function method(value, { createError, path }) {
      if (!value) return true;
      const decimalPart = value.split('.')[1];
      const decimalLength = decimalPart?.length ?? 0;
      if (
        !isInteger &&
        decimalLength > (property.decimalPlaces ?? SUPPORTED_DECIMAL_PLACES)
      ) {
        return createError({
          path,
          message: t(keys.numericDecimalPartMaxLengthExceeded),
          type: 'error',
        });
      }
      return true;
    }
  );

  schema = schema.test(
    'numeric should be between min and max',
    function method(value, { createError, path }) {
      if (!value) return true;
      const valueNum = Big(value);

      if (minValue && !maxValue) {
        const minNum = Big(minValue);
        if (valueNum.cmp(minNum) < 0) {
          return createError({
            path,
            message: t(keys.numericValueLessThanMin, {
              numberType: isInteger ? t('integer') : t('numeric'),
              min: min,
            }),
            type: 'error',
          });
        }
      }

      if (maxValue && !minValue) {
        const maxNum = Big(maxValue);
        if (valueNum.cmp(maxNum) > 0) {
          return createError({
            path,
            message: t(keys.numericValueGreaterThanMax, {
              numberType: isInteger ? t('integer') : t('numeric'),
              max: max,
            }),
            type: 'error',
          });
        }
      }

      if (minValue && maxValue) {
        const minNum = Big(minValue);
        const maxNum = Big(maxValue);

        if (valueNum.cmp(minNum) < 0 || valueNum.cmp(maxNum) > 0) {
          return createError({
            path,
            message: t(keys.numericBetweenMinAndMax, {
              numberType: isInteger ? t('integer') : t('numeric'),
              min: min,
              max: max,
            }),
            type: 'error',
          });
        }
      } else {
        const integerPart = value.split('.')[0] ?? '';
        const integerLength = integerPart.replace(/[-,]/g, '').length;

        if (integerLength > MAX_NUMERIC_STRING_LENGTH) {
          return createError({
            path,
            message: isInteger
              ? t(keys.numericIntegerPartMaxLengthExceeded)
              : t(keys.numericIntegerPartMaxLengthExceededDecimal),
            type: 'error',
          });
        }
      }

      return true;
    }
  );

  return schema;
};

const COMPONENT_TYPE_SCHEMA_MAP: Record<string, SchemaCreator | undefined> = {
  text: createTextSchema,
  alphanumeric: createAlphanumericSchema,
  numeric: createNumericSchema,
};

export const createValidationYupSchema = (
  property: ValidationProperty,
  t: (key: string, params?: Record<string, unknown>) => string,
  messageKeys?: ValidationMessageKeys
): yup.StringSchema<string | null | undefined> => {
  const { componentType } = property;

  const keys = {
    ...DEFAULT_MESSAGE_KEYS,
    ...messageKeys,
  };

  const schemaCreator = COMPONENT_TYPE_SCHEMA_MAP[componentType];

  if (!property.isVisible) {
    return yup.string().nullable().optional();
  }

  if (!schemaCreator) {
    return property.isRequired === true
      ? yup.string().required(t('required_input', { name: property.label }))
      : yup.string().nullable().optional();
  }

  return schemaCreator(property, keys, t);
};

export const useValidationRule = <
  TFieldValues extends FieldValues = FieldValues
>(
  property: ValidationProperty,
  messageKeys?: ValidationMessageKeys
): RegisterOptions<TFieldValues> => {
  const { t } = useTranslation();

  return useMemo(() => {
    const yupSchema = createValidationYupSchema(property, t, messageKeys);

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validate: async (value: any) => {
        try {
          await yupSchema.validate(value);
          return true;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (error instanceof ValidationError) {
            return error.message;
          }
          return error?.message || t('system_error_input');
        }
      },
    };
  }, [property, t, messageKeys]);
};
